import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Paper,
  rem,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { LinksGroup } from "./NavbarLinksGroup/NavbarLinksGroup.jsx";
import { useUIStore } from "../../stores/UIStore.js";
import {
  IconAi,
  IconAiAgent,
  IconAiAgents,
  IconMailForward,
  IconMessages,
  IconPlaylistAdd,
  IconPlus,
  IconRobot,
  IconRocket,
  IconSparkle2,
  IconSparkles,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { useLanguage } from "../../stores/LanguageStore.js";
import { useLeadsStore } from "../../stores/LeadsStore.js";
import { getPermission } from "../../functions/utilFunctions.js";
import { Link, useNavigate } from "react-router-dom";
import { useProfileStore, useSessionStore } from "../../stores/UserStore.js";
import { useEffect } from "react";
import Ressources from "./Ressources.jsx";
import IconProspects from "../../assets/icons/IconProspects.jsx";
import IconSenders from "../../assets/icons/IconSenders.jsx";
import IconReporting from "../../assets/icons/IconReporting.jsx";
import IconLeadsTracking from "../../assets/icons/IconLeadsTracking.jsx";
import IconWhiteLabel from "../../assets/icons/IconWhiteLabel.jsx";
import IconSubscription from "../../assets/icons/IconSubscription.jsx";
import LogoMini from "../LogoMini.jsx";
import LogoHorizontal from "../LogoHorizontal.jsx";
import NavbarLocker from "./NavbarLocker.jsx";
import CampaignsButton from "./CampaignsButton.jsx";
import { useFlowStore } from "../../stores/WorkflowStore.js";
import { modals } from "@mantine/modals";
import BackButton from "../../components/View/BackButton.jsx";
import { clearWorkflow } from "../../pages/protected/workflows/CreateCampaign/flow/flowUtils.jsx";
import IconModels from "../../assets/icons/IconModels.jsx";
import PricingComponent from "./PricingComponent.jsx";

const Navbar = ({ closed }) => {
  const lang = useLanguage((s) => s.language);
  const campaignOpened = useUIStore((s) => s.campaignOpened);
  const setCampaignOpened = useUIStore((s) => s.setCampaignOpened);
  const statsOpened = useUIStore((s) => s.statsOpened);
  const setStatsOpened = useUIStore((s) => s.setStatsOpened);
  const prms = useLeadsStore((s) => s.prms);
  const profile = useProfileStore((s) => s.profile);

  const navigate = useNavigate();
  const handleNewCampaign = (e) => {
    let nodes = useFlowStore.getState().nodes;

    function main() {
      clearWorkflow();
      useFlowStore.getState().setWorkflowKey(Math.random() * 10);
      modals.close("warning-workflow");

      navigate("/campaign/create");
    }

    if (nodes.length > 2 && location.href.includes("/campaign/")) {
      e.preventDefault();
      modals.open({
        modalId: "warning-workflow",
        title: "Attention",
        children: (
          <>
            <Text>{lang.workflows.alert}</Text>

            <Group mt={"lg"} justify="end">
              <BackButton
                onClick={() => modals.close("warning-workflow")}
                size="md"
              />

              <Button onClick={main} size="md">
                {lang.global.ignore}
              </Button>
            </Group>
          </>
        ),
      });
    }
  };

  useEffect(() => {
    if (!getPermission("displaySideMenu")) {
      useUIStore.getState().setAsideCollapsed(true);
    } else {
      useUIStore.getState().setAsideCollapsed(false);
    }
  }, [profile]);

  if (getPermission("displaySideMenu")) {
    if (closed) {
      return (
        <Paper
          component={Stack}
          gap={0}
          className="navbar"
          w={75}
          style={{
            borderRight: "1px solid var(--mantine-color-primary-0)",
          }}
          radius={0}
          h={"100%"}
          pb={6}
          px={0}
          bg={"#fff"}
        >
          <Link to={"/"}>
            <Box h={60} py={4}>
              <LogoMini
                width="80%"
                style={{
                  margin: "0 auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          </Link>

          <Divider size={1} color="#f0f0f0" />

          {getPermission("accessWorkflows") && (
            <Tooltip label={lang.navbar.new_campaign} position="right">
              <Button
                component={Link}
                to={"/campaign/create"}
                id="campaign-button"
                radius={"md"}
                h={44}
                w={"calc(100% - 26px)"}
                ml={13}
                p={0}
                miw={0}
                mt={12}
                justify="center"
                variant="filled"
                pos={"relative"}
                size="md"
                style={{
                  overflow: "visible",
                }}
                onClick={handleNewCampaign}
              >
                <IconRocket />
              </Button>
            </Tooltip>
          )}

          {getPermission("accessContactLists") && (
            <Tooltip label={lang.navbar.new_list} position="right">
              <Button
                component={Link}
                to={"/contact-lists/import"}
                id={"create-list"}
                radius={"md"}
                p={0}
                miw={0}
                h={36}
                variant="light"
                mt={12}
                mb={8}
                justify="center"
                w={"calc(100% - 26px)"}
                ml={13}
              >
                <IconPlaylistAdd size={22} />
              </Button>
            </Tooltip>
          )}

          {profile?.level === "admin" && (
            <LinksGroup
              id={"agents"}
              path={"/agents"}
              icon={IconSparkle2}
              name={"Agents IA"}
              navbarClosed={closed}
            />
          )}

          <LinksGroup
            id={"contact-lists"}
            path={"/contact-lists"}
            icon={IconProspects}
            name={lang.navbar.prospects}
            navbarClosed={closed}
          />

          {getPermission("accessModels") && getPermission("displayForms") && (
            <Ressources
              inNavbar
              navbarClosed={closed}
              opened={campaignOpened}
              setOpened={setCampaignOpened}
            />
          )}

          {getPermission("accessModels") && !getPermission("displayForms") && (
            <LinksGroup
              id={"models"}
              path={"/models"}
              icon={IconModels}
              name={lang.navbar.models}
              navbarClosed={closed}
            />
          )}

          <LinksGroup
            id={"senders"}
            path={"/senders"}
            icon={IconSenders}
            name={lang.navbar.senders}
            navbarClosed={closed}
          />

          <Divider my={4} mx={24} />

          <CampaignsButton
            navbarClosed={closed}
            opened={statsOpened}
            setOpened={setStatsOpened}
          />

          {getPermission("accessPRM") && (
            <LinksGroup
              id={"leads-tracking"}
              path={"/leads-tracking"}
              icon={IconLeadsTracking}
              name={lang.header.navigations.prm.label}
              navbarClosed={closed}
              rightSection={
                prms?.[0]?.new_answerers_since_date_view > 0 && (
                  <Tooltip
                    label={`${prms?.[0]?.new_answerers_since_date_view} ${lang.header.navigations.prm.new_answerers}`}
                  >
                    <Badge
                      size="md"
                      color="primary"
                      pos={"absolute"}
                      top={4}
                      right={2}
                      component={Link}
                      to={"/leads-tracking"}
                      style={{ cursor: "pointer" }}
                      variant="light"
                      // bg={"primary.1"}
                    >
                      {prms?.[0]?.new_answerers_since_date_view}{" "}
                    </Badge>
                  </Tooltip>
                )
              }
            />
          )}

          {getPermission("accessReporting") && (
            <LinksGroup
              id={"reporting"}
              path={"/reporting"}
              icon={IconReporting}
              name={lang.navbar.reports}
              navbarClosed={closed}
            />
          )}

          <Divider mx={"35%"} size={"sm"} mb={8} mt={"auto"} />

          {(profile?.level === "admin" || profile.id === 2094) && (
            <LinksGroup
              id={"resellers"}
              path={"/magileads"}
              name={"Users Magileads"}
              navbarClosed={closed}
              customIcon={
                <ThemeIcon variant={"subtle"} size={32}>
                  <IconUsersGroup />
                </ThemeIcon>
              }
            />
          )}

          {profile?.level === "admin" && (
            <LinksGroup
              id={"assistant"}
              path={"/assistant"}
              name={"Assistant IA"}
              navbarClosed={closed}
              customIcon={
                <ThemeIcon variant={"subtle"} size={32}>
                  <IconMessages />
                </ThemeIcon>
              }
            />
          )}

          {getPermission("displayResellers") && (
            <LinksGroup
              id={"resellers"}
              path={"/resellers"}
              name={lang.resellers.browser_tab_title}
              navbarClosed={closed}
              customIcon={
                <ThemeIcon variant={"subtle"} size={32}>
                  <IconWhiteLabel
                    style={{
                      width: rem(28),
                      height: rem(28),
                      marginRight: -4,
                    }}
                  />
                </ThemeIcon>
              }
            />
          )}

          <PricingComponent closed />
        </Paper>
      );
    }
    return (
      <Paper
        component={Stack}
        gap={0}
        className="navbar"
        w={closed ? 60 : 240}
        radius={0}
        h={"100%"}
        pb={6}
        px={0}
        bg={"#fff"}
        style={{
          borderRight: "1px solid var(--mantine-color-primary-0)",
        }}
      >
        {useSessionStore.getState()?.reseller?.name === "Magileads" ? (
          <Link to={"/"}>
            <Box h={60} pl={14} pr={36}>
              <LogoHorizontal />
            </Box>
          </Link>
        ) : (
          <Link to={"/"}>
            <Box h={60} w={"auto"} py={4}>
              <LogoHorizontal />
            </Box>
          </Link>
        )}

        <NavbarLocker closed={closed} />

        <Divider size={1} color="#f0f0f0" />

        {getPermission("accessWorkflows") && (
          <Button
            component={Link}
            to={"/campaign/create"}
            leftSection={<IconPlus size={24} />}
            id="campaign-button"
            radius={"md"}
            h={44}
            w={"calc(100% - 24px)"}
            mx={12}
            mt={12}
            fz={13.5}
            justify="start"
            variant="filled"
            pos={"relative"}
            size="md"
            style={{
              overflow: "visible",
            }}
            className="shadow-on-hover"
            onClick={handleNewCampaign}
          >
            {lang.navbar.new_campaign}
          </Button>
        )}

        {getPermission("accessContactLists") && (
          <Button
            component={Link}
            to={"/contact-lists/import"}
            leftSection={<IconPlus size={22} />}
            id={"create-list"}
            radius={"md"}
            w={"calc(100% - 24px)"}
            mx={12}
            h={36}
            fz={13}
            variant="light"
            // gradient={{ from: "primary.4", to: "primary.6", deg: 90 }}
            mt={12}
            mb={8}
            justify="start"
            // className="shadow-on-hover"
          >
            {lang.navbar.new_list}
          </Button>
        )}

        {profile?.level === "admin" && (
          <LinksGroup
            id={"agents"}
            path={"/agents"}
            icon={IconSparkle2}
            name={"Agents IA"}
            navbarClosed={closed}
          />
        )}

        <LinksGroup
          id={"contact-lists"}
          path={"/contact-lists"}
          icon={IconProspects}
          name={"Prospects"}
          navbarClosed={closed}
        />

        {getPermission("accessModels") && (
          <Ressources
            inNavbar
            navbarClosed={closed}
            opened={campaignOpened}
            setOpened={setCampaignOpened}
          />
        )}

        <LinksGroup
          id={"senders"}
          path={"/senders"}
          icon={IconSenders}
          name={lang.navbar.senders}
          navbarClosed={closed}
        />

        <Divider my={4} mx={64} />

        {/* {getPermission("accessCampaigns") && (
          <LinksGroup
            id={"statistics"}
            path={"/statistics"}
            icon={IconCampaigns}
            name={lang.navbar.campaigns}
            navbarClosed={closed}
          />
        )} */}

        <CampaignsButton
          navbarClosed={closed}
          opened={statsOpened}
          setOpened={setStatsOpened}
        />

        {getPermission("accessPRM") && (
          <LinksGroup
            id={"leads-tracking"}
            path={"/leads-tracking"}
            icon={IconLeadsTracking}
            name={lang.header.navigations.prm.label}
            navbarClosed={closed}
            rightSection={
              prms?.[0]?.new_answerers_since_date_view > 0 && (
                <Tooltip
                  label={`${prms?.[0]?.new_answerers_since_date_view} ${lang.header.navigations.prm.new_answerers}`}
                >
                  <Badge
                    size="md"
                    color="primary"
                    pos={"absolute"}
                    right={10}
                    component={Link}
                    to={"/leads-tracking"}
                    variant="light"
                  >
                    {prms?.[0]?.new_answerers_since_date_view}{" "}
                  </Badge>
                </Tooltip>
              )
            }
          />
        )}

        {getPermission("accessReporting") && (
          <LinksGroup
            id={"reporting"}
            path={"/reporting"}
            icon={IconReporting}
            name={lang.navbar.reports}
            navbarClosed={closed}
          />
        )}
        <Divider mx={"35%"} size={"sm"} mb={8} mt={"auto"} />

        {profile?.level === "admin" && (
          <LinksGroup
            id={"resellers"}
            path={"/magileads"}
            name={"Users Magileads"}
            navbarClosed={closed}
            customIcon={
              <ThemeIcon variant={"subtle"} size={32}>
                <IconUsersGroup />
              </ThemeIcon>
            }
          />
        )}

        {profile?.level === "admin" && (
          <LinksGroup
            id={"assistant"}
            path={"/assistant"}
            name={"Assistant IA"}
            navbarClosed={closed}
            customIcon={
              <ThemeIcon variant={"subtle"} size={32}>
                <IconMessages />
              </ThemeIcon>
            }
          />
        )}

        {getPermission("displayResellers") && (
          <LinksGroup
            id={"resellers"}
            path={"/resellers"}
            name={lang.resellers.browser_tab_title}
            innerStyle={{
              padding: "8px 0 8px 8px",
              marginBottom: -8,
            }}
            customIcon={
              <ThemeIcon variant={"subtle"}>
                <IconWhiteLabel
                  style={{
                    width: rem(30),
                    height: rem(30),
                  }}
                />
              </ThemeIcon>
            }
          />
        )}

        <PricingComponent />
      </Paper>
    );
  }

  return "";
};

export default Navbar;
